import { Dropdown } from 'primereact/dropdown';
import type { ReactNode } from 'react';

import { cn } from '@shared/lib/cn';

/** Stands for "no filter" in the list when `allLabel` is set (never a real value). */
const ALL = '__all__';

export interface SelectOption<T extends string> {
  label: string;
  value: T;
  disabled?: boolean;
  /** A small badge after the label, in the list and when chosen (e.g. "Last used"). */
  badge?: string;
}

function OptionLabel({ option }: { option: SelectOption<string> }) {
  return (
    <span className="flex items-center gap-2">
      <span>{option.label}</span>
      {option.badge && (
        <span className="rounded-full bg-accent-soft px-2 py-0.5 text-[10px] font-semibold tracking-[0.06em] text-accent">
          {option.badge}
        </span>
      )}
    </span>
  );
}

export interface BaseSelectProps<T extends string> {
  id: string;
  value: T | null;
  options: readonly SelectOption<T>[];
  onChange: (value: T | null) => void;
  onBlur?: () => void;
  placeholder?: string;
  disabled?: boolean;
  invalid?: boolean;
  /** Adds a ✕ to clear back to "none" (filter bars). */
  clearable?: boolean;
  /** Type-to-filter inside the list (long lists such as staff). */
  searchable?: boolean;
  /** Filter bars (prototype): a first "All …" item that stands for no filter (null). */
  allLabel?: string;
  /** Custom content for each item in the open list (e.g. per-item actions). */
  renderItem?: (option: SelectOption<T>) => ReactNode;
  className?: string;
  'aria-label'?: string;
}

export function BaseSelect<T extends string>({
  id,
  value,
  options,
  onChange,
  onBlur,
  placeholder,
  disabled,
  invalid,
  clearable,
  searchable,
  allLabel,
  renderItem,
  className,
  'aria-label': ariaLabel,
}: BaseSelectProps<T>) {
  const badged = options.some((o) => o.badge);
  const list: SelectOption<string>[] = allLabel
    ? [{ label: allLabel, value: ALL }, ...options]
    : [...options];
  const dropdown = (
    <Dropdown
      inputId={id}
      value={allLabel ? (value ?? ALL) : value}
      options={list}
      optionLabel="label"
      optionValue="value"
      optionDisabled="disabled"
      onChange={(e: { value: T | typeof ALL | null | undefined }) => {
        onChange(e.value === ALL ? null : (e.value ?? null));
      }}
      onBlur={onBlur}
      placeholder={placeholder}
      disabled={disabled}
      invalid={invalid}
      showClear={clearable}
      filter={searchable}
      resetFilterOnHide
      // Badges need their own rendering; plain lists keep PrimeReact's default.
      itemTemplate={
        renderItem
          ? (o: SelectOption<T>) => renderItem(o)
          : badged
            ? (o: SelectOption<string>) => <OptionLabel option={o} />
            : undefined
      }
      valueTemplate={
        badged
          ? (o: SelectOption<string> | null) => (o ? <OptionLabel option={o} /> : (placeholder ?? ''))
          : undefined
      }
      // Name both the focusable input and the trigger (PrimeReact would use the placeholder).
      // PrimeReact also renders a hidden, unfocusable native <select> for form posts; it
      // duplicates the combobox, so screen readers skip it.
      ariaLabel={ariaLabel}
      pt={{
        select: { 'aria-hidden': true },
        ...(ariaLabel ? { input: { 'aria-label': ariaLabel }, trigger: { 'aria-label': ariaLabel } } : {}),
      }}
      className={cn('w-full', className)}
    />
  );
  // PrimeReact turns pointer events off on a disabled control (and forces its cursor), so the
  // "not allowed" cursor comes from a wrapper.
  return disabled ? <div className="w-full cursor-not-allowed">{dropdown}</div> : dropdown;
}
